import { clamp, cloneDeep, difference, isNaN, isNil, noop, round } from "lodash-es";
import { useCallback, useState } from "react";
import { Field, useForm } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { Input, ListGroupItem, Progress, Row, Col, FormText, Card, ListGroup } from "reactstrap";
import { TextInput } from "../../components/inputs/final_form/TextInput";
import { humanizeString } from "../../util/formatters";
import { FieldsetWrapper } from "../SubmitRightTestForm";
import { SelectInput } from "../../components/inputs/final_form/SelectInput";
import { DateInput } from "../../components/inputs/final_form/DateInput";
import { produce } from "immer";
import { required } from "../../util/validators";

const DEFAULT_MIN = 1;

const getMinCountForCode = () => {
  return DEFAULT_MIN;
};

const syncCptCodes = (target, sourceCptCodeId, { disable = false, amount = undefined, multiplier = undefined }) => {
  return cloneDeep(target).map((targetCode) => {
    if (targetCode.value === sourceCptCodeId) {
      targetCode.isDisabled = disable;

      if (!isNil(amount)) {
        targetCode.amountAvailable = amount;

        if (amount === 0) targetCode.isDisabled = true;
      }

      if (!isNil(multiplier)) {
        targetCode.multiplier = multiplier;
      }
    }

    return targetCode;
  });
};

const StatusDependentTestField = ({
  formLink = undefined,
  name,
  displayCheck,
  requiredCondition,
  displayCondition,
  children,
  ...props
}) => {
  if (!displayCondition) {
    return null;
  }

  const fieldName = isNil(formLink) ? `${name}` : `${formLink}.${name}`;
  return (
    <>
      <Field name={fieldName} required={requiredCondition} validate={requiredCondition ? required : noop} {...props}>
        {children}
      </Field>
    </>
  );
};

export const StatusTheCaseFields = ({
  verifiedCptCodeOptions,
  approvedCptCodes,
  deniedCptCodes,
  cleanClaimCptCodes,
  isCodeSpecificResult,
  status,
  displayStatusMap,
  requiredStatusMap,
  index,
  request,
  denialReasonCodeOptions,
  messageObj,
  formLink,
}) => {
  const form = useForm();

  // State for Approved CPT Codes - CodeSpecificResult
  const [approvedCodesOptions, setApprovedCodesOptions] = useState(cloneDeep(verifiedCptCodeOptions));
  const [selectedApprovedCodes, setSelectedApprovedCodes] = useState(cloneDeep([approvedCptCodes].flatten));

  // State for Denied CPT Codes - CodeSpecificResult
  const [deniedCodesOptions, setDeniedCodesOptions] = useState(cloneDeep(verifiedCptCodeOptions));
  const [selectedDeniedCodes, setSelectedDeniedCodes] = useState([deniedCptCodes].flatten);

  // State for Clean Claim CPT Codes - CodeSpecificResult
  const [cleanClaimCodesOptions, setCleanClaimCodesOptions] = useState(cloneDeep(verifiedCptCodeOptions));
  const [selectedCleanClaimCodes, setSelectedCleanClaimCodes] = useState([cleanClaimCptCodes].flatten);

  const displayForStatus = useCallback((field) => (displayStatusMap[status] || []).includes(field), [status, displayStatusMap]);
  const requiredForStatus = useCallback((field) => (requiredStatusMap[status] || []).includes(field), [status, requiredStatusMap]);

  const displayAuthorizationFields = useCallback(
    (field) => {
      return (approvedCptCodes?.length > 0 && isCodeSpecificResult) || displayForStatus(field);
    },
    [approvedCptCodes, displayForStatus, isCodeSpecificResult]
  );

  const requiresAuthorizationFields = useCallback(
    (field) => {
      return (approvedCptCodes?.length > 0 && isCodeSpecificResult) || requiredForStatus(field);
    },
    [approvedCptCodes?.length, requiredForStatus, isCodeSpecificResult]
  );

  const displayDenialReason = useCallback(
    (field) => {
      return (deniedCptCodes?.length > 0 && isCodeSpecificResult) || displayForStatus(field);
    },
    [deniedCptCodes?.length, displayForStatus, isCodeSpecificResult]
  );

  const requiresDenialReason = useCallback(
    (field) => {
      return (deniedCptCodes?.length > 0 && isCodeSpecificResult) || requiredForStatus(field);
    },
    [deniedCptCodes?.length, requiredForStatus, isCodeSpecificResult]
  );

  const codesCategoriesMap = {
    approved: {
      options: approvedCodesOptions,
      optionStateFn: setApprovedCodesOptions,
      selectedCodes: selectedApprovedCodes,
      selectedStateFn: setSelectedApprovedCodes,
      reflect: ["denied", "cleanClaim"],
      formLink: "expandedApprovedCptCodes",
    },
    denied: {
      options: deniedCodesOptions,
      optionStateFn: setDeniedCodesOptions,
      selectedCodes: selectedDeniedCodes,
      selectedStateFn: setSelectedDeniedCodes,
      reflect: ["approved", "cleanClaim"],
      formLink: "expandedDeniedCptCodes",
    },
    cleanClaim: {
      options: cleanClaimCodesOptions,
      optionStateFn: setCleanClaimCodesOptions,
      selectedCodes: selectedCleanClaimCodes,
      selectedStateFn: setSelectedCleanClaimCodes,
      reflect: ["approved", "denied"],
      formLink: "expandedCleanClaimCptCodes",
    },
  };

  // This method handles the MultiSelects onChange
  const updateCodeCategories = ({ category, newCodes, oldCodes }) => {
    // We grab the removed value
    const removedCodes =
      difference(oldCodes, newCodes) &&
      cloneDeep(codesCategoriesMap[category].options).filter((cptCode) =>
        difference(oldCodes, newCodes).find((selectedCode) => selectedCode === cptCode.value)
      );

    // We grab the added values
    const addedCodes =
      difference(newCodes, oldCodes) &&
      cloneDeep(codesCategoriesMap[category].options).filter((cptCode) =>
        difference(newCodes, oldCodes).find((selectedCode) => selectedCode === cptCode.value)
      );

    // We grab the collection of selected values
    const selectedValues = cloneDeep(codesCategoriesMap[category].options).filter((c) => newCodes.find((cptCode) => c.value === cptCode));

    // Processing removed codes
    removedCodes.map((removedCode) => {
      // Here we grab the code categories that we need to reflect/update
      const reflexCategories = codesCategoriesMap[category].reflect;

      // Now we grab their selected codes
      const firstPossibleSelectedCodeCategory = cloneDeep(codesCategoriesMap[reflexCategories[0]].selectedCodes)?.find(
        (c) => c.value === removedCode.value
      );
      const secondPossibleSelectedCodeCategory = cloneDeep(codesCategoriesMap[reflexCategories[1]].selectedCodes)?.find(
        (c) => c.value === removedCode.value
      );

      const originalCptCode = cloneDeep(verifiedCptCodeOptions)?.find((c) => c.value === removedCode.value);
      const updatedAmount =
        Number(originalCptCode.amountAvailable) -
        Number(firstPossibleSelectedCodeCategory?.multiplier ?? 0) -
        Number(secondPossibleSelectedCodeCategory?.multiplier ?? 0);

      // Now we update the main category options
      codesCategoriesMap[category].optionStateFn((previousOptions) =>
        syncCptCodes(previousOptions, removedCode.value, { multiplier: 0, amount: updatedAmount })
      );

      // Here we check if the first possible selected code category exists
      if (firstPossibleSelectedCodeCategory) {
        // If it exists we sync both options and selected values
        // Here we calc the amount to update
        const updatedAmount = Number(firstPossibleSelectedCodeCategory.amountAvailable) + Number(removedCode.multiplier);

        // Sync options
        codesCategoriesMap[reflexCategories[0]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, firstPossibleSelectedCodeCategory.value, { disable: false, amount: updatedAmount })
        );
        // Sync selected codes units
        codesCategoriesMap[reflexCategories[0]].selectedStateFn((previousDeniedCodes) =>
          syncCptCodes(previousDeniedCodes, firstPossibleSelectedCodeCategory.value, { disable: false, amount: updatedAmount })
        );
      } else {
        // Otherwise, it means that we only need to sync the options
        const updatedAmount =
          Number(originalCptCode.amountAvailable) -
          Number(firstPossibleSelectedCodeCategory?.multiplier ?? 0) -
          Number(secondPossibleSelectedCodeCategory?.multiplier ?? 0);

        codesCategoriesMap[reflexCategories[0]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, removedCode.value, { disable: false, amount: updatedAmount })
        );
      }

      // Here we check if the first possible selected code category exists
      if (secondPossibleSelectedCodeCategory) {
        // If it exists we sync both options and selected values
        // Here we calc the amount to update
        const updatedAmount = Number(secondPossibleSelectedCodeCategory.amountAvailable) + Number(removedCode.multiplier);

        codesCategoriesMap[reflexCategories[1]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, secondPossibleSelectedCodeCategory.value, { disable: false, amount: updatedAmount })
        );
        codesCategoriesMap[reflexCategories[1]].selectedStateFn((previousCleanClaimCodes) =>
          syncCptCodes(previousCleanClaimCodes, secondPossibleSelectedCodeCategory.value, { disable: false, amount: updatedAmount })
        );
      } else {
        // Otherwise, it means that we only need to sync the options
        const updatedAmount =
          Number(originalCptCode.amountAvailable) -
          Number(firstPossibleSelectedCodeCategory?.multiplier ?? 0) -
          Number(secondPossibleSelectedCodeCategory?.multiplier ?? 0);

        codesCategoriesMap[reflexCategories[1]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, removedCode.value, { disable: false, amount: updatedAmount })
        );
      }
    });

    // Processing added codes
    const parsedValues = selectedValues.map((cptCode) => {
      // If we find the selected code on the newly added values we take action
      if (addedCodes.find((code) => code.value === cptCode.value)) {
        // Grab the original cpt code to be the reference
        const originalCptCode = cloneDeep(verifiedCptCodeOptions).find((c) => c.value === cptCode.value);

        const reflexCategories = codesCategoriesMap[category].reflect;

        // Disable on the first category
        codesCategoriesMap[reflexCategories[0]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, cptCode.value, { disable: true, amount: 0 })
        );

        // Disable on the second
        codesCategoriesMap[reflexCategories[1]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, cptCode.value, { disable: true, amount: 0 })
        );

        const parsedCode = cloneDeep(cptCode);

        parsedCode.multiplier = cptCode.amountAvailable;

        // Now we sync the main category options to update the amount to 1
        codesCategoriesMap[category].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, cptCode.value, { multiplier: cptCode.amountAvailable })
        );

        return parsedCode;
      }

      return cptCode;
    });

    // Now we update the selected codes for the main category with the new updated values
    codesCategoriesMap[category].selectedStateFn((_previousApprovedCodes) => parsedValues);

    // Now we set the form key to parse the processed values
    form.change(`${formLink}.${codesCategoriesMap[category].formLink}`, parsedValues);
  };

  // This method handles the CPT code unit onChange
  const updateCodeCategoriesUnits = ({ category, codeId, count }) => {
    count = round(count);

    if (isNaN(count)) {
      return false;
    }

    const reflexCategories = codesCategoriesMap[category].reflect;

    const originalCptCode = cloneDeep(verifiedCptCodeOptions).find((c) => c.value === codeId);

    const firstPossibleSelectedCodeCategory = cloneDeep(codesCategoriesMap[reflexCategories[0]].selectedCodes)?.find(
      (c) => c.value === codeId
    );
    const secondPossibleSelectedCodeCategory = cloneDeep(codesCategoriesMap[reflexCategories[1]].selectedCodes)?.find(
      (c) => c.value === codeId
    );

    const maxCount = Number(originalCptCode.amountAvailable);
    const minCount = getMinCountForCode();

    const clampedCount = clamp(count, minCount, maxCount);

    if (maxCount === clampedCount) {
      // Now we exhausted the CPT Code, then we disable the cpt code on the another options - DENIED - DCC
      const updatedAmount = maxCount - clampedCount;

      // If we don't have the code selected we just update the options
      if (!firstPossibleSelectedCodeCategory)
        codesCategoriesMap[reflexCategories[0]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, codeId, { disable: true, amount: updatedAmount })
        );

      if (!secondPossibleSelectedCodeCategory)
        codesCategoriesMap[reflexCategories[1]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, codeId, { disable: true, amount: updatedAmount })
        );
    } else {
      // Now we consumed part of the amount of the CPT Code, we change the cpt code amount on the another options - DENIED - DCC
      if (firstPossibleSelectedCodeCategory) {
        // Taking the another multipliers into consideration
        // This means that we have this code selected on first category codes, then we subtract the amount from options AND selectedCodes
        const updatedAmount = maxCount - clampedCount - Number(secondPossibleSelectedCodeCategory?.multiplier ?? 0);

        codesCategoriesMap[reflexCategories[0]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, codeId, { disable: false, amount: updatedAmount })
        );

        codesCategoriesMap[reflexCategories[0]].selectedStateFn((previousCodes) =>
          syncCptCodes(previousCodes, codeId, { disable: false, amount: updatedAmount })
        );
      } else {
        // This means that we don't have this code selected on first category codes, then we subtract from the original
        // Taking the another multipliers into consideration
        const updatedAmount = maxCount - clampedCount - (secondPossibleSelectedCodeCategory?.multiplier ?? 0);

        codesCategoriesMap[reflexCategories[0]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, codeId, { disable: false, amount: updatedAmount })
        );
      }

      if (secondPossibleSelectedCodeCategory) {
        // This means that we have this code selected on second reflex category codes, then we subtract the amount from options AND selectedCodes
        // Taking the another multipliers into consideration
        const updatedAmount = maxCount - clampedCount - Number(firstPossibleSelectedCodeCategory?.multiplier ?? 0);

        codesCategoriesMap[reflexCategories[1]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, codeId, { disable: false, amount: updatedAmount })
        );
        codesCategoriesMap[reflexCategories[1]].selectedStateFn((previousCodes) =>
          syncCptCodes(previousCodes, codeId, { disable: false, amount: updatedAmount })
        );
      } else {
        // This means that we don't have this code selected on DCC codes, then we subtract from the original
        // Taking the another multipliers into consideration
        const updatedAmount = maxCount - clampedCount - Number(firstPossibleSelectedCodeCategory?.multiplier ?? 0);

        codesCategoriesMap[reflexCategories[1]].optionStateFn((previousOptions) =>
          syncCptCodes(previousOptions, codeId, { disable: false, amount: updatedAmount })
        );
      }
    }

    // Now we run the calculation for the current amount available
    const currentAmountAvailable =
      maxCount - Number(firstPossibleSelectedCodeCategory?.multiplier ?? 0) - Number(secondPossibleSelectedCodeCategory?.multiplier ?? 0);

    // Now we build new data to to reflect the correct amount
    const newCptCodes = produce(codesCategoriesMap[category].selectedCodes, (draft) => {
      draft.find((c) => c.value === codeId).multiplier = clampedCount;
      draft.find((c) => c.value === codeId).amountAvailable = currentAmountAvailable;
    });

    // Now we update the main category options and selected codes
    codesCategoriesMap[category].optionStateFn((previousOptions) =>
      syncCptCodes(previousOptions, codeId, { disable: false, amount: currentAmountAvailable, multiplier: clampedCount })
    );

    codesCategoriesMap[category].selectedStateFn((_previousCodes) => newCptCodes);

    form.change(`${formLink}.${codesCategoriesMap[category].formLink}`, newCptCodes);
  };

  // Handlers for Approved CPT Codes - CodeSpecificResult
  const handleApprovedCodesCount = (selectedCodeId, count) => {
    // Here we call the handler for CPT code units change
    updateCodeCategoriesUnits({ category: "approved", codeId: selectedCodeId, count: count });
  };

  const handleApprovedCodesChange = (selectedCodes, previousCodes) => {
    // Here we call the update function passing the category and onChange attrs
    updateCodeCategories({ category: "approved", newCodes: selectedCodes, oldCodes: previousCodes });
  };

  // Handlers for Denied CPT Codes - CodeSpecificResult
  const handleDeniedCodesCount = (selectedCodeId, count) => {
    updateCodeCategoriesUnits({ category: "denied", codeId: selectedCodeId, count: count });
  };

  const handleDeniedCodesChange = (selectedCodes, previousCodes) => {
    // Here we call the update function passing the category and onChange attrs
    updateCodeCategories({ category: "denied", newCodes: selectedCodes, oldCodes: previousCodes });
  };

  // Handlers for Clean Claim CPT Codes - CodeSpecificResult
  const handleCleanClaimCodesCount = async (selectedCodeId, count) => {
    updateCodeCategoriesUnits({ category: "cleanClaim", codeId: selectedCodeId, count: count });
  };

  const handleCleanClaimCodesChange = (selectedCodes, previousCodes) => {
    // Here we call the update function passing the category and onChange attrs
    updateCodeCategories({ category: "cleanClaim", newCodes: selectedCodes, oldCodes: previousCodes });
  };

  return (
    <>
      <Field
        component={TextInput}
        label="Status"
        format={(val) => humanizeString(val || "None")}
        name={`${formLink}.status`}
        disabled={true}
      />
      <Field
        component={TextInput}
        label="Substatus"
        format={(val) => humanizeString(val || "None")}
        name={`${formLink}.substatus`}
        disabled={true}
      />
      <StatusDependentTestField
        requiredCondition={requiredForStatus("submissionReferenceNumber")}
        displayCondition={displayForStatus("submissionReferenceNumber")}
        formLink={formLink}
        name="submissionReferenceNumber"
        component={TextInput}
        label="Reference Number"
        initialValue={request.submission_reference_number}
        format={(val) => val?.trim()}
      />
      <FieldsetWrapper labelText="Approval Fields" displayCondition={isCodeSpecificResult}>
        <StatusDependentTestField
          requiredCondition={requiredForStatus("approvedCptCodes")}
          displayCondition={displayForStatus("approvedCptCodes")}
          formLink={formLink}
          name="approvedCptCodes"
          component={SelectInput}
          isMulti
          options={approvedCodesOptions}
          label="Approved CPT Codes"
          placeholder="Select one or more CPT Codes..."
          valueKey="code"
          labelKey="code"
        >
          {selectedApprovedCodes && selectedApprovedCodes.length > 0 && (
            <>
              <FormText>Select the correct amount of units for the chosen CPT codes.</FormText>

              <Card className="mt-2 mb-0">
                <ListGroup flush>
                  {selectedApprovedCodes.map((code) => {
                    const maxCap = cloneDeep(approvedCodesOptions).find((cptCode) => cptCode.value === code.value);
                    const amountAvailable = maxCap?.amountAvailable;

                    return (
                      <ListGroupItem key={code.value}>
                        <div className="d-flex flex-row justify-content-between">
                          <div className="d-flex flex-row gap-1 align-items-center">
                            <code>{code.value}</code> &times; {code.multiplier}
                          </div>

                          <div className="d-flex flex-column">
                            <div className="d-flex flex-row gap-1">
                              <Input
                                id={code.value}
                                value={code.multiplier}
                                onInput={(e) => handleApprovedCodesCount(code.value, e.target.valueAsNumber)}
                                bsSize="sm"
                                max={amountAvailable}
                                min={DEFAULT_MIN}
                                type="number"
                              />
                            </div>
                          </div>
                        </div>
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              </Card>
            </>
          )}
          <OnChange name={`${formLink}.approvedCptCodes`}>{handleApprovedCodesChange}</OnChange>
        </StatusDependentTestField>

        {selectedApprovedCodes && selectedApprovedCodes.length > 0 && <hr className="mt-0 mb-3" />}

        <Row>
          <Col>
            <StatusDependentTestField
              requiredCondition={requiresAuthorizationFields("authorizationDate")}
              displayCondition={displayAuthorizationFields("authorizationDate")}
              formLink={formLink}
              name="authorizationDate"
              component={DateInput}
              label="Authorization Date"
              initialValue={request.authorization_date}
            />
          </Col>
          <Col>
            <StatusDependentTestField
              requiredCondition={requiresAuthorizationFields("authorizationNumber")}
              displayCondition={displayAuthorizationFields("authorizationNumber")}
              formLink={formLink}
              name="authorizationNumber"
              component={TextInput}
              label="Authorization Number"
              placeholder="XXXXXXXXX"
              initialValue={request.authorization_number}
              format={(val) => val?.trim()}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <StatusDependentTestField
              requiredCondition={requiredForStatus("authorizationEffectiveDate")}
              displayCondition={displayAuthorizationFields("authorizationEffectiveDate")}
              formLink={formLink}
              name="authorizationEffectiveDate"
              component={DateInput}
              label="Authorization Effective Date"
              initialValue={request.authorization_effective_date}
            />
          </Col>
          <Col>
            <StatusDependentTestField
              requiredCondition={requiredForStatus("authorizationExpirationDate")}
              displayCondition={displayAuthorizationFields("authorizationExpirationDate")}
              formLink={formLink}
              name="authorizationExpirationDate"
              component={DateInput}
              label="Authorization Expiration Date"
              initialValue={request.authorization_expiration_date}
            />
          </Col>
        </Row>
      </FieldsetWrapper>
      <FieldsetWrapper labelText="Denial Fields" displayCondition={isCodeSpecificResult}>
        <StatusDependentTestField
          requiredCondition={requiredForStatus("deniedCptCodes")}
          displayCondition={displayForStatus("deniedCptCodes")}
          formLink={formLink}
          name="deniedCptCodes"
          component={SelectInput}
          isMulti
          options={deniedCodesOptions}
          label="Denied CPT Codes"
          placeholder="Select one or more CPT Codes..."
          valueKey="code"
          labelKey="code"
        >
          {selectedDeniedCodes && selectedDeniedCodes.length > 0 && (
            <>
              <FormText>Select the correct amount of units for the chosen CPT codes.</FormText>

              <Card className="mt-2 mb-0">
                <ListGroup flush>
                  {selectedDeniedCodes.map((code) => {
                    const maxCap = cloneDeep(deniedCodesOptions).find((cptCode) => cptCode.value === code.value);
                    const amountAvailable = maxCap?.amountAvailable;

                    return (
                      <ListGroupItem key={code.value}>
                        <div className="d-flex flex-row justify-content-between">
                          <div className="d-flex flex-row gap-1 align-items-center">
                            <code>{code.value}</code> &times; {code.multiplier}
                          </div>

                          <div className="d-flex flex-column">
                            <div className="d-flex flex-row gap-1">
                              <Input
                                id={code.value}
                                value={code.multiplier}
                                onInput={(e) => handleDeniedCodesCount(code.value, e.target.valueAsNumber)}
                                bsSize="sm"
                                max={amountAvailable}
                                min={0}
                                type="number"
                              />
                            </div>
                          </div>
                        </div>
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              </Card>
            </>
          )}

          <OnChange name={`${formLink}.deniedCptCodes`}>{handleDeniedCodesChange}</OnChange>
        </StatusDependentTestField>

        {selectedDeniedCodes && selectedDeniedCodes.length > 0 && <hr className="mt-0 mb-3" />}

        <StatusDependentTestField
          requiredCondition={requiresDenialReason("denialReasonCode")}
          displayCondition={displayDenialReason("denialReasonCode")}
          formLink={formLink}
          name="denialReasonCode"
          component={SelectInput}
          label="Denial Reason Code"
          options={denialReasonCodeOptions}
          initialValue={request.denial_reason_code}
          disabled={messageObj?.denial_reason_code ? true : false}
        />
      </FieldsetWrapper>
      <FieldsetWrapper labelText="Clean Claim Fields" displayCondition={isCodeSpecificResult}>
        <StatusDependentTestField
          requiredCondition={requiredForStatus("documentedCleanClaimCptCodes")}
          displayCondition={displayForStatus("documentedCleanClaimCptCodes")}
          formLink={formLink}
          name="documentedCleanClaimCptCodes"
          component={SelectInput}
          isMulti
          options={cleanClaimCodesOptions}
          label="Documented Clean Claim CPT Codes"
          placeholder="Select one or more CPT Codes..."
          valueKey="code"
          labelKey="code"
        >
          {selectedCleanClaimCodes && selectedCleanClaimCodes.length > 0 && (
            <>
              <FormText>Select the correct amount of units for the chosen CPT codes.</FormText>

              <Card className="mt-2 mb-0">
                <ListGroup flush>
                  {selectedCleanClaimCodes.map((code) => {
                    const maxCap = cloneDeep(cleanClaimCodesOptions).find((cptCode) => cptCode.value === code.value);
                    const amountAvailable = maxCap?.amountAvailable;

                    return (
                      <ListGroupItem key={code.value}>
                        <div className="d-flex flex-row justify-content-between">
                          <div className="d-flex flex-row gap-1 align-items-center">
                            <code>{code.value}</code> &times; {code.multiplier}
                          </div>

                          <div className="d-flex flex-column">
                            <div className="d-flex flex-row gap-1">
                              <Input
                                id={code.value}
                                value={code.multiplier}
                                onInput={(e) => handleCleanClaimCodesCount(code.value, e.target.valueAsNumber)}
                                bsSize="sm"
                                max={amountAvailable}
                                min={0}
                                type="number"
                              />
                            </div>
                          </div>
                        </div>
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              </Card>
            </>
          )}
        </StatusDependentTestField>

        <OnChange name={`${formLink}.documentedCleanClaimCptCodes`}>{handleCleanClaimCodesChange}</OnChange>
      </FieldsetWrapper>
      <StatusDependentTestField
        requiredCondition={requiredForStatus("expectedCloseDate")}
        displayCondition={displayForStatus("expectedCloseDate")}
        formLink={formLink}
        name="expectedCloseDate"
        index={index}
        component={DateInput}
        label="Expected Close Date"
      />
    </>
  );
};
